import { useRef, type Dispatch, type FC, type SetStateAction } from "react";
import Badge from "../Badge";
import Text from "../Text";
import KrunshScore from "../KrunshScore";
import { BikeIcon, SquareArrowOutUpRight, StarIcon } from "lucide-react";
import { cn, triggerEvent } from "@/lib/utils";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import useDragAndScroll from "@/hooks/useDragAndScroll";
import { removeRestaurantFromFavoriteList } from "@/store/userFavoriteListState";

type RestaurantCardProps = {
  id?: string;
  name: string;
  address: string;
  rate: string;
  tags: string[];
  image?: string;
  platform: string;
  krunshScore: number;
  deliveryTime: number;
  firstSeen?: string | null;
  onClick?: () => void;
  addFavoriteAction?: boolean;
  canBeFavorited?: boolean;
  setAddFavoriteThisReastaurantId?: Dispatch<SetStateAction<string | null>>;
  isFavorite?: boolean;
  linkToRestaurant?: string;
};

const RestaurantCard: FC<RestaurantCardProps> = ({
  id,
  name,
  address,
  rate,
  tags,
  image,
  platform,
  krunshScore,
  deliveryTime,
  firstSeen,
  onClick,
  canBeFavorited,
  setAddFavoriteThisReastaurantId,
  isFavorite,
  linkToRestaurant,
}) => {
  const { t } = useTranslation();
  const isNewRestaurant = firstSeen
    ? dayjs().diff(dayjs(firstSeen), "week") < 3
    : false;
  const refElementToScroll = useRef<HTMLDivElement>(null);
  const { onMouseDown, onMouseLeave, onMouseMoveCapture, onMouseUp } =
    useDragAndScroll({
      elementToScroll: refElementToScroll,
      direction: "horizontal",
    });

  return (
    <div
      style={{
        transition: "scale 0.2s",
        display: "block",
        boxShadow: "0px 2px 4px 0px #00000014",
      }}
      className={cn(
        "bg-[white] rounded-[16px] p-[16px]",
        onClick ? "hover:scale-103 cursor-pointer" : undefined,
      )}
      onClick={() => {
        if (onClick) {
          triggerEvent("Card Clicked", {
            store_id: id,
          });
          onClick();
        }
      }}
    >
      <div className="relative">
        {image ? (
          <img
            src={image}
            alt={`image of ${name}`}
            className="aspect-3/2 object-cover rounded-[16px]"
          />
        ) : (
          <div className="flex items-center bg-[#dedede] justify-center w-[100%] aspect-3/2 object-cover rounded-[16px]">
            <Text>{t("missing_image")}</Text>
          </div>
        )}

        <div className="absolute top-[16px] left-[16px] flex items-center">
          <img
            width={32}
            height={32}
            className="rounded-[50%]"
            src={`/partner/${platform}.png`}
            alt={`logo of ${platform}`}
          />
        </div>
        {canBeFavorited && (
          <div
            className="absolute top-[22px] right-[20px]"
            onClick={(e) => {
              if (!isFavorite) {
                setAddFavoriteThisReastaurantId?.(id ?? null);
                e.stopPropagation();
              } else {
                if (id) {
                  removeRestaurantFromFavoriteList({ restaurantId: id });
                  e.stopPropagation();
                }
              }
            }}
          >
            <img
              width={22}
              height={20}
              src={
                isFavorite
                  ? "/svg/favorite-heart-red.svg"
                  : "/svg/favorite-heart.svg"
              }
            />
          </div>
        )}
        <div className="absolute bottom-[16px] left-[16px] flex items-center gap-[8px]">
          {isNewRestaurant ? (
            <Badge>
              <Text fontWeight={600} fontSize={14} className="uppercase">
                {t("new")}
              </Text>
            </Badge>
          ) : (
            <>
              <Badge>
                {rate === "0" ? (
                  <Text fontWeight={600} fontSize={14}>
                    {t("not_rated")}
                  </Text>
                ) : (
                  <>
                    <StarIcon height={16} width={16} color="#758195" />
                    <Text fontWeight={600} fontSize={14}>
                      {rate}
                    </Text>
                  </>
                )}
              </Badge>
              <Badge>
                <BikeIcon height={16} width={16} color="#758195" />
                <Text fontWeight={600} fontSize={14}>
                  {`${deliveryTime} min`}
                </Text>
              </Badge>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-[12px] mt-[24px]">
        <KrunshScore value={krunshScore} />
        <div className="grid">
          <Text
            fontSize={14}
            fontWeight={600}
            style={{ lineHeight: "20px" }}
            className="truncate"
          >
            {name}
          </Text>
          <Text
            fontSize={12}
            fontWeight={400}
            color="#535862"
            className="truncate"
            style={{ lineHeight: "16px" }}
          >
            {address}
          </Text>
        </div>
      </div>

      {tags.length !== 0 && (
        <div
          className="flex gap-[8px] overflow-x-auto mt-[16px]"
          ref={refElementToScroll}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          onMouseDown={onMouseDown}
          onMouseMoveCapture={(e) =>
            onMouseMoveCapture(e as unknown as MouseEvent)
          }
        >
          {tags.map((tag) => (
            <Badge key={`${address}-${tag}`}>
              <Text fontSize={14} fontWeight={400}>
                {tag}
              </Text>
            </Badge>
          ))}
        </div>
      )}

      {linkToRestaurant && (
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={linkToRestaurant}
          className="flex items-center gap-[8px] mt-[16px]"
        >
          <SquareArrowOutUpRight color="#44546F" height={18} width={18} />
          <Text fontSize={14} fontWeight={600}>
            {t("show_menu")}
          </Text>
        </a>
      )}
    </div>
  );
};

export default RestaurantCard;
